import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { BackendApiService } from './backend-api.service';
import { UserService } from './core/user.service';
import { UserStatusService } from '../modules/shared/header/statistics-dropdown/user-status.service';
import { resolveProfileImageUrl } from '../utils/utils.function';
import { MODULE_IDS } from '../constants/module-ids.constant';
import {
  GlobalSearchApiHit,
  GlobalSearchCacheEntry,
  GlobalSearchItem,
  GlobalSearchModuleConfig,
  GlobalSearchModulePage,
  GlobalSearchModuleResult,
  GlobalSearchModuleTheme,
  GlobalSearchPageResponse,
  GlobalSearchPreviewResponse
} from '../modules/shared/interfaces/global-search.interface';

const RECENT_SEARCHES_KEY = 'ps_global_search_recent';
const ANALYTICS_KEY = 'ps_global_search_analytics';
const MAX_RECENT = 10;
const CACHE_TTL_MS = 30_000;  // 30 seconds
const MAX_CACHE_ENTRIES = 20;

/** Backend rules (GlobalSearchRequest): search is required, min 2, max 100 characters. */
export const GLOBAL_SEARCH_MIN_LENGTH = 2;
export const GLOBAL_SEARCH_MAX_LENGTH = 100;

/**
 * One fixed color per team role, so the same role always reads the same
 * color across every search result (a Sales Rep badge is always this blue,
 * an Estimator badge always this purple, etc.) — not a per-person color,
 * which would be meaningless since the same person can hold different
 * roles on different records. Role strings here match exactly what the
 * backend sends (IndexSearchDocument::resolveTeamMember()'s $role argument,
 * app/Jobs/Search/IndexSearchDocument.php) — keep the two in sync if a role
 * label ever changes there.
 */
const TEAM_ROLE_COLORS: Record<string, string> = {
  'Assigned To': '#FF6600',
  'Sales Rep': '#3B82F6',
  'Secondary Sales Rep': '#0EA5E9',
  'Primary Sales Rep': '#3B82F6',
  'Estimator': '#8B5CF6',
  'Project Manager': '#EC4899',
};
const DEFAULT_TEAM_ROLE_COLOR = '#FF6600';

/**
 * Global Search Service
 * Talks to the finalized backend Global Search API (Elasticsearch-backed):
 *   GET /api/v1/search?search={term}                                   preview, max 5 per module
 *   GET /api/v1/search/{module}?search={term}&page={n}&per_page={n}   full module results, paginated
 * Authorization/tenant scoping is enforced entirely by the backend; nothing here filters or scopes results.
 */
@Injectable({ providedIn: 'root' })
export class GlobalSearchService {

  /** Module-specific color themes for icon circles (keys are the backend module keys) */
  private readonly moduleThemes: Record<string, GlobalSearchModuleTheme> = {
    customer: { iconBg: '#EFF6FF', iconColor: '#3B82F6' },   // blue
    bid:      { iconBg: '#F0FDF4', iconColor: '#22C55E' },   // green
    job:      { iconBg: '#FFF7ED', iconColor: '#F97316' },   // orange
    user:     { iconBg: '#F5F3FF', iconColor: '#8B5CF6' },   // purple
    task:     { iconBg: '#FEF2F2', iconColor: '#EF4444' },   // red
    ticket:   { iconBg: '#FFFBEB', iconColor: '#F59E0B' },   // amber
    vendor:   { iconBg: '#FDF2F8', iconColor: '#EC4899' },   // pink
    warehouse:{ iconBg: '#F0FDFA', iconColor: '#0D9488' },   // teal
    proposal: { iconBg: '#EEF2FF', iconColor: '#6366F1' },   // indigo
    note:     { iconBg: '#FEFCE8', iconColor: '#EAB308' },   // yellow
    document: { iconBg: '#F8FAFC', iconColor: '#64748B' },   // slate
    event:    { iconBg: '#ECFEFF', iconColor: '#06B6D4' },   // cyan
    email:    { iconBg: '#FDF4FF', iconColor: '#A855F7' },   // purple
    reminder: { iconBg: '#FFF7ED', iconColor: '#F59E0B' },   // amber
    vendor_quote: { iconBg: '#F0FDF4', iconColor: '#16A34A' }, // green (distinct from bid's brighter green)
    milestone: { iconBg: '#F7FEE7', iconColor: '#65A30D' },  // lime
  };

  /** Result cache — (user + query) → results with TTL */
  private cache = new Map<string, GlobalSearchCacheEntry>();

  /**
   * The approved modules, in the backend's result order. Detail routes are the real
   * Angular routes (customer/bid/job/staff/task/ticket/vendor/warehouse/proposal/note).
   * Documents have no detail route of their own — see resolveDocumentRoute().
   */
  private readonly moduleConfigs: GlobalSearchModuleConfig[] = [
    { key: 'customer', label: 'Customers', icon: 'people',              detailRoute: (id) => `/customers/customer/${id}` },
    { key: 'bid',      label: 'Bids',      icon: 'gavel',               detailRoute: (id) => `/bids/bid/${id}` },
    { key: 'job',      label: 'Jobs',      icon: 'work',                detailRoute: (id) => `/jobs/detail/${id}` },
    { key: 'user',     label: 'Staff',     icon: 'badge',               detailRoute: (id) => `/hr/staff/${id}` },
    // Task, Ticket and Note results open that module's listing page with the
    // record's own detail sidebar auto-opened (?openId=, read by
    // ListTaskComponent/ListTicketComponent/ListNoteComponent — the same
    // dialog their own row click already opens), instead of navigating to
    // the individual record page. Note's own detail page in particular shows
    // static placeholder content rather than the real record; Task's and
    // Ticket's work fine, but all three are routed the same way per request.
    { key: 'task',     label: 'Tasks',     icon: 'task_alt',            detailRoute: (id) => `/tasks?openId=${id}` },
    { key: 'ticket',   label: 'Tickets',   icon: 'confirmation_number', detailRoute: (id) => `/tickets?openId=${id}` },
    { key: 'vendor',   label: 'Vendors',   icon: 'store',               detailRoute: (id) => `/vendors/vendor/${id}` },
    { key: 'warehouse',label: 'Warehouses',icon: 'warehouse',           detailRoute: (id) => `/warehouses/warehouse/${id}` },
    { key: 'proposal', label: 'Proposals', icon: 'description',         detailRoute: (id) => `/proposals/detail/${id}` },
    { key: 'note',     label: 'Notes',     icon: 'note',                detailRoute: (id) => `/notes?openId=${id}` },
    { key: 'document', label: 'Documents', icon: 'folder',              detailRoute: () => '' },
    // Calendar has one consistent standalone route (/calendar); the embedded
    // per-Job/Bid calendar widget is not a sensible landing target since an
    // event isn't always tied to a single parent record. CalendarListComponent
    // reads ?openEvent=, fetches the event, and opens its own existing
    // openEventDialog() — same self-contained "fetch by id, no dependency on
    // any tab being loaded" shape as Task/Ticket/Note's ?openId=.
    { key: 'event',    label: 'Events',    icon: 'event',               detailRoute: (id) => `/calendar?openEvent=${id}` },
    // record_id itself is sign-encoded by the backend (Inbox positive,
    // Outbox negative — see IndexSearchDocument::buildEmail()), so it's
    // passed straight through unchanged; EmailLayoutComponent derives
    // inbox/outbox from the sign the same way GlobalSearchService's own
    // authorizedEmailIds() does. No async resolve step needed (unlike
    // Task/Document) since Email always lands on the same /email route.
    { key: 'email',    label: 'Emails',    icon: 'mail',                detailRoute: (id) => `/email?emailId=${id}` },
    // Reminder has no dedicated page/route at all — its click handler
    // (global-search.component.ts / global-search-results.component.ts)
    // opens the existing AddReminderModalComponent dialog directly, right
    // where the user already is, instead of navigating. detailRoute is
    // never actually used for 'reminder' (both click handlers special-case
    // it before reaching item.route), same as Document's placeholder.
    { key: 'reminder', label: 'Reminders', icon: 'notifications',       detailRoute: () => '' },
    // Vendor Quote has no standalone page either — same as Reminder/Document,
    // detailRoute is never actually used (buildItem() special-cases this key
    // and builds the real route from the hit's own vendor_id/parent_type/
    // parent_id directly, no extra network round trip needed — see
    // resolveVendorQuoteRoute()).
    { key: 'vendor_quote', label: 'Vendor Quotes', icon: 'request_quote', detailRoute: () => '' },
    // Milestone (App\Models\ModuleDataMilestone) has no standalone page of its
    // own either — same placeholder shape as Document/Reminder/Vendor Quote
    // above. The real destination is resolved at click time via
    // resolveMilestoneParentSidebarRoute() (global-search.component.ts),
    // which lands on the milestone's actual parent (Job/Bid/Customer/Vendor/
    // Staff/Warehouse). Unlike Task/Ticket/Note, there is currently no
    // dedicated "Milestones" tab on ANY of those detail pages (verified
    // directly — milestones today only surface via a small card on each
    // page's own Summary tab, MilestonePhaseCardComponent/
    // ProjectTimelineActivityComponent), so this lands on that Summary tab
    // (tab=0) instead — the closest existing equivalent. 'flag' matches the
    // icon already used for the "Attach Milestone" action-menu item
    // elsewhere in the app (job-action-dropdown etc.).
    { key: 'milestone', label: 'Milestones', icon: 'flag', detailRoute: () => '' },
  ];

  /**
   * Where a Document opens, keyed by the class name of the record it is attached to
   * (`parent_type`, e.g. "App\Models\Task"). Keyed by class name rather than the numeric
   * module id, because module ids used elsewhere in the app disagree with the values stored
   * on documents. Parents that are not one of the 10 search modules (Change Order, Submittal,
   * Product, Purchase Order) still have real detail routes, so they are included.
   */
  private readonly documentParentRoutes: Record<string, (id: number | string) => string> = {
    // Each of these 7 detail pages reads `?tab=N` into a mat-tab-group bound
    // to a `tabIndex`, and every tab is rendered unconditionally (permission-
    // gated tabs are only ever [disabled], never removed), so the Documents
    // tab's index is stable for every viewer. Indices were extracted by
    // stripping HTML comments first (a naive grep over the raw markup can
    // both miscount a genuinely commented-out tab as live — see Job's
    // now-removed "Phases" tab below — and miss a live one whose
    // [attr.aria-label] isn't the tag's first attribute, e.g. the Activity
    // Log tab on Vendor/Staff/Customer, which starts with [disabled]):
    // Customer 9, Bid 10, Job 14, Vendor 11, Staff 11 (detail.component.html /
    // detail-bid / job-detail / vendor-detail / staff-detail-page).
    //
    // Job specifically routes through job-detail.component.ts (the actual
    // `jobs/detail/:id` route target — NOT detail-job.component.ts, an
    // unused sibling with a completely different, shorter tab list that this
    // work mistakenly wired up first and then reverted).
    //
    // Ticket, Task and Note are different: a Task/Ticket/Note is itself
    // always attached to some bigger record (its `reference_type`/
    // `reference_id` — Job, Bid, Customer, Vendor, Staff, Product,
    // Warehouse, Subcontractor, Service, Jobsite, Proposal...), and that is
    // where its documents really belong. These three entries are the
    // fallback only — used when resolveGrandparentRoute() (see below) can't
    // resolve a better destination (network error, or the reference type has
    // no known tab). Task and Note still land on their own working
    // Documents/Attachments tab in that case; Ticket has no separate
    // Documents tab at all (embedded inside its only tab, Summary) so it
    // falls back to its default page.
    Customer:      (id) => `/customers/customer/${id}?tab=9`,
    Bid:           (id) => `/bids/bid/${id}?tab=10`,
    Job:           (id) => `/jobs/detail/${id}?tab=14`,
    User:          (id) => `/hr/staff/${id}?tab=11`,
    Task:          (id) => `/tasks/task/${id}?tab=13`,
    Ticket:        (id) => `/tickets/ticket/${id}`,
    Vendor:        (id) => `/vendors/vendor/${id}?tab=10`,
    Proposal:      (id) => `/proposals/detail/${id}`,
    Note:          (id) => `/notes/note/${id}?tab=1`,
    ChangeOrder:   (id) => `/change-orders/view/${id}`,
    Submittal:     (id) => `/submittals/view/${id}`,
    Product:       (id) => `/products/product/${id}`,
    PurchaseOrder: (id) => `/purchase-order/detail/${id}`,
  };

  constructor(
    private backendApi: BackendApiService,
    private userService: UserService,
    private userStatusService: UserStatusService,
    private sanitizer: DomSanitizer
  ) {}

  /** Normalise a raw query the way the backend validates it: trimmed, at most 100 characters. */
  normalizeQuery(query: string | null | undefined): string {
    return (query ?? '').trim().slice(0, GLOBAL_SEARCH_MAX_LENGTH);
  }

  /**
   * Preview search across all 10 modules (max 5 per module, ranked by the backend).
   * Errors are NOT swallowed here: the caller decides how to present them (503 / 422 / network).
   */
  search(query: string): Observable<GlobalSearchModuleResult[]> {
    const term = this.normalizeQuery(query);
    const cacheKey = this.cacheKey(term);

    const cached = this.cache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < CACHE_TTL_MS) {
      return of(cached.results);
    }

    // Fresh params object each call: BackendApiService.getApi() mutates it.
    return (this.backendApi.getApi('search', { search: term }, false) as Observable<GlobalSearchPreviewResponse>).pipe(
      map(response => this.mapPreview(response)),
      map(results => {
        this.setCacheEntry(cacheKey, term, results);
        return results;
      })
    );
  }

  /** One page of "View all" results for a single module. */
  searchModule(moduleKey: string, query: string, page = 1, perPage = 20): Observable<GlobalSearchModulePage> {
    const config = this.getModuleConfig(moduleKey);
    const term = this.normalizeQuery(query);

    return (this.backendApi.getApi(
      `search/${moduleKey}`,
      { search: term, page, per_page: perPage },
      false
    ) as Observable<GlobalSearchPageResponse>).pipe(
      map(response => {
        const data = response?.data;
        const hits = data?.results ?? [];
        return {
          module: moduleKey,
          moduleLabel: config?.label ?? moduleKey,
          moduleIcon: config?.icon ?? 'search',
          items: config ? hits.map(hit => this.buildItem(config, hit)) : [],
          total: data?.total ?? 0,
          currentPage: data?.current_page ?? page,
          perPage: data?.per_page ?? perPage,
          lastPage: data?.last_page ?? 1,
          from: data?.from ?? null,
          to: data?.to ?? null,
          hasMore: !!data?.has_more,
        };
      })
    );
  }

  /** Backend preview response (object keyed by module) → ordered, non-empty module groups. */
  private mapPreview(response: GlobalSearchPreviewResponse): GlobalSearchModuleResult[] {
    const data = response?.data ?? {};
    const groups: GlobalSearchModuleResult[] = [];

    for (const config of this.moduleConfigs) {
      const group = data[config.key];
      const hits = group?.results ?? [];
      if (hits.length === 0) continue;

      const total = group?.total ?? hits.length;
      groups.push({
        module: config.key,
        moduleLabel: config.label,
        moduleIcon: config.icon,
        items: hits.map(hit => this.buildItem(config, hit)),
        total,
        hasMore: total > hits.length,
      });
    }
    return groups;
  }

  private buildItem(config: GlobalSearchModuleConfig, hit: GlobalSearchApiHit): GlobalSearchItem {
    const id = hit.record_id ?? '';
    const parentClassName = hit.parent_type?.split('\\').pop();
    const needsGrandparent = config.key === 'document' && !!parentClassName
      && (parentClassName in this.grandparentEndpoints);
    return {
      id,
      title: hit.title ?? '',
      subtitle: hit.identifier ?? '',
      description: hit.description ?? null,
      amount: hit.amount ?? null,
      assigneeId: hit.assignee_id ?? null,
      assigneeName: hit.assignee_name ?? null,
      team: hit.team ?? [],
      customerName: hit.customer_name ?? null,
      bidTypeName: hit.bid_type_name ?? null,
      dueDate: hit.due_date ?? null,
      startDate: hit.start_date ?? null,
      scopeName: hit.scope_name ?? null,
      route: config.key === 'document' ? this.resolveDocumentRoute(hit)
        : config.key === 'vendor_quote' ? this.resolveVendorQuoteRoute(hit)
        : config.detailRoute(id),
      module: config.key,
      moduleLabel: config.label,
      moduleIcon: config.icon,
      isFolder: config.key === 'document' ? this.isFolderDocument(hit) : undefined,
      grandparentType: needsGrandparent ? parentClassName : undefined,
      grandparentId: needsGrandparent ? (hit.parent_id ?? undefined) : undefined,
    };
  }

  /**
   * HTML-safe highlight, shared by the header dropdown (GlobalSearchComponent)
   * and the "View all" results page (GlobalSearchResultsComponent) so both
   * bold search matches identically — the single place this logic lives,
   * per Global Search's existing "no duplicated search/highlight logic"
   * convention. Text is split on the term FIRST and every piece is
   * HTML-escaped separately, so record content from the API can never
   * inject markup, and a term like "a" can never cut an entity such as
   * &amp; in half. Angular's own sanitizer still runs on the [innerHTML]
   * result at each call site.
   */
  highlightText(text: string | null | undefined, term: string): string {
    if (!text) return '';
    const escapeHtml = (s: string) => s
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    const trimmedTerm = (term || '').trim();
    if (!trimmedTerm) return escapeHtml(text);

    const regex = new RegExp(`(${trimmedTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text
      .split(regex)
      .map((part, i) => (i % 2 === 1 ? `<mark class="search-highlight">${escapeHtml(part)}</mark>` : escapeHtml(part)))
      .join('');
  }

  /**
   * Two-letter initials for a team-member avatar badge — the single shared
   * implementation for the header dropdown (GlobalSearchComponent) and the
   * "View all" results page (GlobalSearchResultsComponent), both of which
   * also mirror AvatarGroupComponent.getInitials() (shared/avatar-group/
   * avatar-group.component.ts) without importing that full component (see
   * getTeamAvatars()'s docblock for why).
   */
  getInitials(name: string | null | undefined): string {
    if (!name) return '';
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  /**
   * Builds the small set of stacked-avatar badges for one search item's
   * `team` — the lightweight equivalent of the real app-avatar-group
   * component (shared/avatar-group) used by the Bids/Jobs list pages' own
   * "Team" column, reimplemented here (rather than imported) because the
   * Global Search components are standalone and AvatarGroupComponent is
   * not. Shared by both the header dropdown and the "View all" results
   * page so they can never drift apart in how they merge/display a team.
   *
   * Same person appearing under more than one role (e.g. a Bid's Estimator
   * who is also its Sales Rep) is merged into ONE badge whose tooltip lists
   * every role — the same "mergeDuplicates" behavior AvatarGroupComponent
   * itself applies on the real Team column. Grouped by id when present,
   * else by lowercased name (mirrors AvatarGroupComponent.getAvatarKey()).
   *
   * Falls back to a single-entry array built from assigneeId/assigneeName
   * when `team` is empty/absent but an assignee is present — belt-and-
   * braces for any older cached item that predates the `team` field; a
   * freshly-indexed item always already has `team` populated identically.
   */
  getTeamAvatars(item: { team?: Array<{ id?: number | string | null; name: string; role: string; status?: string | null; profile_image_url?: string | null }> | null; assigneeId?: number | string | null; assigneeName?: string | null }): Array<{ name: string; roles: string[]; initials: string; tooltip: string; color: string; statusSvg: SafeHtml | undefined; profileImageUrl: string }> {
    const source = (item.team && item.team.length)
      ? item.team
      : (item.assigneeName ? [{ id: item.assigneeId ?? null, name: item.assigneeName, role: 'Assigned To', status: null as string | null, profile_image_url: null as string | null }] : []);

    const order: string[] = [];
    const byKey = new Map<string, { name: string; roles: string[]; status: string | null; profileImageUrl: string }>();

    for (const member of source) {
      const name = (member?.name || '').trim();
      if (!name) continue;

      const key = member.id != null && member.id !== ''
        ? `id:${member.id}`
        : `name:${name.toLowerCase()}`;

      let entry = byKey.get(key);
      if (!entry) {
        entry = { name, roles: [], status: null, profileImageUrl: '' };
        byKey.set(key, entry);
        order.push(key);
      }

      const role = (member.role || '').trim();
      if (role && !entry.roles.includes(role)) {
        entry.roles.push(role);
      }

      // Same person's own real presence status, however many roles they
      // hold on this record — a single value, not per-role (matches
      // IndexSearchDocument::resolveTeamMember()'s 'status', same column
      // every occurrence of this person carries).
      if (!entry.status && member.status) {
        entry.status = member.status;
      }

      // Same person's own real uploaded profile photo — same meaning as
      // `status` above, a single value per person regardless of role
      // (IndexSearchDocument::resolveTeamMember()'s 'profile_image_url').
      if (!entry.profileImageUrl && member.profile_image_url) {
        entry.profileImageUrl = resolveProfileImageUrl(member.profile_image_url);
      }
    }

    return order.map((key) => {
      const entry = byKey.get(key)!;
      return {
        name: entry.name,
        roles: entry.roles,
        initials: this.getInitials(entry.name),
        tooltip: entry.roles.length ? `${entry.roles.join(', ')} — ${entry.name}` : entry.name,
        // Colored by this person's FIRST role on this record (the order
        // each module lists its roles in — see resolveTeamMember() call
        // order per buildX() method) — a person merged across multiple
        // roles still gets one consistent badge color, not one per role.
        color: TEAM_ROLE_COLORS[entry.roles[0]] ?? DEFAULT_TEAM_ROLE_COLOR,
        // Small presence-status badge, same icon/source as the real
        // Bids/Jobs list "Team" column's own avatars (AvatarGroupComponent
        // .calculateStatusSvg()) — defaults to "available" when this
        // person has no stored status, same default that component uses,
        // so the same person never shows inconsistently with/without a
        // badge depending on which screen you're on.
        statusSvg: this.statusSvgFor(entry.status),
        // Same real uploaded photo app-avatar-group already shows for this
        // exact person elsewhere (Bid/Job detail header) — empty string
        // (not null) when they have none, so the template's existing
        // falsy `@if (avatars[0].profileImageUrl)` check needs no extra
        // null-handling.
        profileImageUrl: entry.profileImageUrl,
      };
    });
  }

  /** See getTeamAvatars()'s statusSvg docblock — same resolution AvatarGroupComponent.calculateStatusSvg() uses. */
  private statusSvgFor(statusSlug: string | null): SafeHtml | undefined {
    const status = this.userStatusService?.getStatusBySlug(statusSlug || 'available');
    return status?.profileShownSvg
      ? this.sanitizer.bypassSecurityTrustHtml(status.profileShownSvg)
      : undefined;
  }

  /**
   * A Document's direct parent may itself be a Task, Ticket or Note — records
   * that are always attached to some bigger one (their own `reference_type`/
   * `reference_id`: Job, Bid, Customer, Vendor, Staff, Product, Warehouse,
   * Subcontractor, Service, Jobsite, Proposal...), which is where the document
   * really belongs. Endpoint paths match each module's own detail service
   * (TaskDataService/TicketDataService/NoteDataService) — not reused directly
   * here to avoid a cross-module DI dependency for one field each needs.
   */
  private readonly grandparentEndpoints: Record<string, (id: number | string) => string> = {
    Task: (id) => `tasks/${id}`,
    Ticket: (id) => `ticket/${id}`,
    Note: (id) => `notes/${id}`,
  };

  /**
   * Maps a Task/Ticket/Note's real parent (`module.id` from its own detail
   * response, the same catalog as MODULE_IDS) to a route — reusing the exact
   * tab indices already verified for that module's own Global Search entry.
   * A type with no known Documents tab (Product, Warehouse, Subcontractor,
   * Service, Jobsite, Proposal has no tab either but does have a route) is
   * simply absent here, which the caller treats as "use the fallback route".
   */
  private readonly moduleIdToRoute: Record<number, (id: number) => string> = {
    [MODULE_IDS.JOB]:      (id) => `/jobs/detail/${id}?tab=14`,
    [MODULE_IDS.BID]:      (id) => `/bids/bid/${id}?tab=10`,
    [MODULE_IDS.CUSTOMER]: (id) => `/customers/customer/${id}?tab=9`,
    [MODULE_IDS.VENDOR]:   (id) => `/vendors/vendor/${id}?tab=10`,
    [MODULE_IDS.STAFF]:    (id) => `/hr/staff/${id}?tab=11`,
    [MODULE_IDS.PROPOSAL]: (id) => `/proposals/detail/${id}`,
  };

  /**
   * Resolves the real destination for a Document whose parent is a Task,
   * Ticket or Note: fetches that record's own detail (one extra call, made
   * only when the item is actually clicked — never at search-result-render
   * time), reads its `module.id` + `reference.id`, and maps to a route.
   * Emits null (never throws) when the fetch fails or the reference type has
   * no known route, so the caller can fall back to the item's own `route`.
   */
  resolveGrandparentRoute(grandparentType: string, grandparentId: number): Observable<string | null> {
    const endpoint = this.grandparentEndpoints[grandparentType]?.(grandparentId);
    if (!endpoint) return of(null);

    return this.backendApi.getApi(endpoint, {}, false).pipe(
      map((res: any) => {
        const moduleId = res?.data?.module?.id;
        const referenceId = res?.data?.reference?.id;
        if (!moduleId || !referenceId) return null;
        const builder = this.moduleIdToRoute[moduleId];
        return builder ? builder(referenceId) : null;
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Where a Task/Ticket/Note's own sidebar opens once its parent module is
   * known — a PARALLEL set of maps to `moduleIdToRoute`, not a reuse of it:
   * `moduleIdToRoute` sends a Document to the parent's *Documents tab*
   * (`?tab=`), but a Task/Ticket/Note search result needs the parent's own
   * detail page with BOTH the right tab selected AND its own popup
   * auto-opened on top of it (`?openTask=`/`?openTicket=`/`?openNote=` +
   * `&tab=`), read by that page's own `ngOnInit` (see job-detail.component.ts
   * / detail-bid.component.ts / customer detail.component.ts) the same way
   * ListTaskComponent/ListTicketComponent/ListNoteComponent already read
   * `?openId=` on their own standalone list pages today.
   *
   * Tab indices are counted from each parent's actual `<mat-tab>` DOM order
   * (verified directly, not from any of that page's own — sometimes stale —
   * hardcoded tab-index constants):
   *   Job:      Tasks=10, Tickets=11, Notes=12
   *   Bid:      Tasks=7,  Tickets=N/A (no Tickets tab on Bid at all —
   *             lands on Bid with no &tab=, popup still opens on top),
   *             Notes=8
   *   Customer: Tasks=7,  Tickets=6,  Notes=5
   *   Vendor:   Tasks=9,  Tickets=7,  Notes=8
   *   Staff:    Tasks=6,  Tickets=5,  Notes=10
   *   Warehouse:Tasks=6,  Tickets=5,  Notes=4
   *
   * Scope: Job, Bid, Customer, Vendor, Staff, Warehouse — Proposal is the
   * sole exclusion (it has no Tasks/Tickets/Notes tab at all).
   */
  private readonly moduleIdToTaskSidebarRoute: Record<number, (parentId: number, taskId: number | string) => string> = {
    [MODULE_IDS.JOB]: (parentId, taskId) => `/jobs/detail/${parentId}?openTask=${taskId}&tab=10`,
    [MODULE_IDS.BID]: (parentId, taskId) => `/bids/bid/${parentId}?openTask=${taskId}&tab=7`,
    [MODULE_IDS.CUSTOMER]: (parentId, taskId) => `/customers/customer/${parentId}?openTask=${taskId}&tab=7`,
    [MODULE_IDS.VENDOR]: (parentId, taskId) => `/vendors/vendor/${parentId}?openTask=${taskId}&tab=9`,
    [MODULE_IDS.STAFF]: (parentId, taskId) => `/hr/staff/${parentId}?openTask=${taskId}&tab=6`,
    [MODULE_IDS.WAREHOUSE]: (parentId, taskId) => `/warehouses/warehouse/${parentId}?openTask=${taskId}&tab=6`,
  };

  private readonly moduleIdToTicketSidebarRoute: Record<number, (parentId: number, ticketId: number | string) => string> = {
    [MODULE_IDS.JOB]: (parentId, ticketId) => `/jobs/detail/${parentId}?openTicket=${ticketId}&tab=11`,
    // Bid has no Tickets tab at all — land on the Bid with no &tab= (falls
    // back to whatever tab the page defaults to), popup still opens on top.
    [MODULE_IDS.BID]: (parentId, ticketId) => `/bids/bid/${parentId}?openTicket=${ticketId}`,
    [MODULE_IDS.CUSTOMER]: (parentId, ticketId) => `/customers/customer/${parentId}?openTicket=${ticketId}&tab=6`,
    [MODULE_IDS.VENDOR]: (parentId, ticketId) => `/vendors/vendor/${parentId}?openTicket=${ticketId}&tab=7`,
    [MODULE_IDS.STAFF]: (parentId, ticketId) => `/hr/staff/${parentId}?openTicket=${ticketId}&tab=5`,
    [MODULE_IDS.WAREHOUSE]: (parentId, ticketId) => `/warehouses/warehouse/${parentId}?openTicket=${ticketId}&tab=5`,
  };

  private readonly moduleIdToNoteSidebarRoute: Record<number, (parentId: number, noteId: number | string) => string> = {
    [MODULE_IDS.JOB]: (parentId, noteId) => `/jobs/detail/${parentId}?openNote=${noteId}&tab=12`,
    [MODULE_IDS.BID]: (parentId, noteId) => `/bids/bid/${parentId}?openNote=${noteId}&tab=8`,
    [MODULE_IDS.CUSTOMER]: (parentId, noteId) => `/customers/customer/${parentId}?openNote=${noteId}&tab=5`,
    [MODULE_IDS.WAREHOUSE]: (parentId, noteId) => `/warehouses/warehouse/${parentId}?openNote=${noteId}&tab=4`,
    [MODULE_IDS.VENDOR]: (parentId, noteId) => `/vendors/vendor/${parentId}?openNote=${noteId}&tab=8`,
    [MODULE_IDS.STAFF]: (parentId, noteId) => `/hr/staff/${parentId}?openNote=${noteId}&tab=9`,
  };

  /**
   * Where a Milestone search result lands: its real parent (Job/Bid/
   * Customer/Vendor/Staff/Warehouse — the same six parent types
   * authorizedMilestoneIds() supports on the backend), on tab=0 (Summary) —
   * the tab every one of these detail pages already opens on by default,
   * and the one place a milestone already surfaces today
   * (MilestonePhaseCardComponent/ProjectTimelineActivityComponent). There is
   * no dedicated "Milestones" tab on any parent page yet (verified directly
   * against job-detail.component.html/detail-bid.component.html), so unlike
   * moduleIdToTaskSidebarRoute this intentionally carries no extra tab index
   * and opens no popup (no single-milestone detail dialog exists to open).
   */
  private readonly moduleIdToMilestoneSidebarRoute: Record<number, (parentId: number) => string> = {
    [MODULE_IDS.JOB]: (parentId) => `/jobs/detail/${parentId}?tab=0`,
    [MODULE_IDS.BID]: (parentId) => `/bids/bid/${parentId}?tab=0`,
    [MODULE_IDS.CUSTOMER]: (parentId) => `/customers/customer/${parentId}?tab=0`,
    [MODULE_IDS.VENDOR]: (parentId) => `/vendors/vendor/${parentId}?tab=0`,
    [MODULE_IDS.STAFF]: (parentId) => `/hr/staff/${parentId}?tab=0`,
    [MODULE_IDS.WAREHOUSE]: (parentId) => `/warehouses/warehouse/${parentId}?tab=0`,
  };

  /**
   * Resolves where a Milestone search result should actually navigate —
   * same shape as resolveTaskParentSidebarRoute() below, hitting the
   * Milestone equivalent of GET tasks/{id} (ModuleDataMilestoneController::
   * show(), added alongside this), which returns the same
   * {module:{id}, reference:{id}} shape TaskController::show() already
   * does. Emits null (never throws) when the parent isn't a supported type
   * or the fetch fails, so the caller can show a toast instead of
   * navigating nowhere (there is no item.route fallback worth falling back
   * to for Milestone — see moduleConfigs' 'milestone' entry).
   */
  resolveMilestoneParentSidebarRoute(milestoneId: number | string): Observable<string | null> {
    return this.backendApi.getApi(`module-data-milestones/${milestoneId}`, {}, false).pipe(
      map((res: any) => {
        const moduleId = res?.data?.module?.id;
        const referenceId = res?.data?.reference?.id;
        if (!moduleId || !referenceId) return null;
        const builder = this.moduleIdToMilestoneSidebarRoute[moduleId];
        return builder ? builder(referenceId) : null;
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Resolves where a Task search result should actually navigate: the
   * Task's own parent, with the parent's Tasks tab selected and the Task's
   * sidebar set to auto-open — rather than the generic `/tasks?openId=`
   * listing page. Reuses the same fetch-by-id call as
   * resolveGrandparentRoute() (one extra request, made only on click) but
   * builds a different kind of route from the result. Emits null (never
   * throws) when the parent isn't a supported type or the fetch fails, so
   * the caller falls back to the item's own `route`.
   */
  resolveTaskParentSidebarRoute(taskId: number | string): Observable<string | null> {
    const endpoint = this.grandparentEndpoints['Task'](taskId);
    return this.backendApi.getApi(endpoint, {}, false).pipe(
      map((res: any) => {
        const moduleId = res?.data?.module?.id;
        const referenceId = res?.data?.reference?.id;
        if (!moduleId || !referenceId) return null;
        const builder = this.moduleIdToTaskSidebarRoute[moduleId];
        return builder ? builder(referenceId, taskId) : null;
      }),
      catchError(() => of(null))
    );
  }

  /** Same as resolveTaskParentSidebarRoute(), for Ticket. */
  resolveTicketParentSidebarRoute(ticketId: number | string): Observable<string | null> {
    const endpoint = this.grandparentEndpoints['Ticket'](ticketId);
    return this.backendApi.getApi(endpoint, {}, false).pipe(
      map((res: any) => {
        const moduleId = res?.data?.module?.id;
        const referenceId = res?.data?.reference?.id;
        if (!moduleId || !referenceId) return null;
        const builder = this.moduleIdToTicketSidebarRoute[moduleId];
        return builder ? builder(referenceId, ticketId) : null;
      }),
      catchError(() => of(null))
    );
  }

  /** Same as resolveTaskParentSidebarRoute(), for Note. */
  resolveNoteParentSidebarRoute(noteId: number | string): Observable<string | null> {
    const endpoint = this.grandparentEndpoints['Note'](noteId);
    return this.backendApi.getApi(endpoint, {}, false).pipe(
      map((res: any) => {
        const moduleId = res?.data?.module?.id;
        const referenceId = res?.data?.reference?.id;
        if (!moduleId || !referenceId) return null;
        const builder = this.moduleIdToNoteSidebarRoute[moduleId];
        return builder ? builder(referenceId, noteId) : null;
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Documents have no standalone detail route, so open the record they are attached to.
   * Returns null when the parent type is unknown/missing — the UI shows such a hit as
   * non-clickable rather than navigating somewhere wrong.
   */
  private resolveDocumentRoute(hit: GlobalSearchApiHit): string | null {
    if (!hit.parent_type || hit.parent_id == null) return null;
    const className = hit.parent_type.split('\\').pop() ?? '';
    const builder = this.documentParentRoutes[className];
    return builder ? builder(hit.parent_id) : null;
  }

  /**
   * A Vendor Quote hit's real parent is fully known already — vendor_id,
   * itemable_type and itemable_id all come attached directly on the hit
   * (GlobalSearchService::attachVendorQuoteParents(), a live DB lookup, not
   * anything stored in the ES document), so unlike Task/Ticket/Note this
   * needs no extra network round trip at click time. Lands on the Bid/Job
   * the quote was quoted against when known, else falls back to its
   * required parent Vendor — tab indices verified directly from each
   * page's own <mat-tab> DOM order (Bid=6, Job=8, Vendor=1). Opens the
   * quote's own edit dialog on top via `?openVendorQuote=`, read by
   * detail-bid.component.ts / job-detail.component.ts / vendor-detail
   * .component.ts's own ngOnInit/loadVendor(), the same shape as
   * ?openTask=/?openTicket=/?openNote=.
   */
  private resolveVendorQuoteRoute(hit: GlobalSearchApiHit): string | null {
    const quoteId = hit.record_id;
    if (quoteId == null) return null;

    const itemableClass = hit.parent_type?.split('\\').pop();
    if (itemableClass === 'Bid' && hit.parent_id != null) {
      return `/bids/bid/${hit.parent_id}?openVendorQuote=${quoteId}&tab=6`;
    }
    if (itemableClass === 'Job' && hit.parent_id != null) {
      return `/jobs/detail/${hit.parent_id}?openVendorQuote=${quoteId}&tab=8`;
    }
    if (hit.vendor_id != null) {
      return `/vendors/vendor/${hit.vendor_id}?openVendorQuote=${quoteId}&tab=1`;
    }
    return null;
  }

  /**
   * A Document hit has no `type` (file/folder) field in the search index — only the
   * metadata used for matching is indexed (IndexSearchDocument::DOCUMENT_FIELDS).
   * Inferred here instead: a folder's name never carries a file extension, and a
   * real file's always does (file_type is only ever populated for `type = 'file'`
   * rows), so this needs no new indexed field or reindex.
   */
  private isFolderDocument(hit: GlobalSearchApiHit): boolean {
    const name = hit.identifier || hit.title || '';
    return !/\.[a-z0-9]{2,5}$/i.test(name);
  }

  /** Get the color theme for a module */
  getModuleTheme(moduleKey: string): GlobalSearchModuleTheme | undefined {
    return this.moduleThemes[moduleKey];
  }

  /** Per-user storage key so recent searches / analytics never leak between accounts. */
  private userKey(base: string): string {
    return `${base}_${this.userService.getID() ?? 'anon'}`;
  }

  private cacheKey(term: string): string {
    return `${this.userService.getID() ?? 'anon'}|${term.toLowerCase()}`;
  }

  /** Retrieve recent searches from localStorage */
  getRecentSearches(): string[] {
    try {
      const stored = localStorage.getItem(this.userKey(RECENT_SEARCHES_KEY));
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  /** Save a search query to recent searches (prepend, dedupe, cap at MAX_RECENT) */
  saveRecentSearch(query: string): void {
    if (!query || !query.trim()) return;
    const trimmed = this.normalizeQuery(query);
    let recent = this.getRecentSearches();
    recent = recent.filter(r => r !== trimmed);
    recent.unshift(trimmed);
    if (recent.length > MAX_RECENT) {
      recent = recent.slice(0, MAX_RECENT);
    }
    localStorage.setItem(this.userKey(RECENT_SEARCHES_KEY), JSON.stringify(recent));
  }

  /** Remove a single recent search term */
  removeRecentSearch(term: string): void {
    let recent = this.getRecentSearches();
    recent = recent.filter(r => r !== term);
    localStorage.setItem(this.userKey(RECENT_SEARCHES_KEY), JSON.stringify(recent));
  }

  /** Clear all recent searches */
  clearRecentSearches(): void {
    localStorage.removeItem(this.userKey(RECENT_SEARCHES_KEY));
  }

  /** Get a module's config (label, icon, route builder) by backend key */
  getModuleConfig(moduleKey: string): GlobalSearchModuleConfig | undefined {
    return this.moduleConfigs.find(c => c.key === moduleKey);
  }

  /** Clear the result cache */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Track a search result selection for analytics.
   * Stores to localStorage under `ps_global_search_analytics_{userId}`.
   */
  trackSelection(query: string, item: GlobalSearchItem): void {
    try {
      const key = this.userKey(ANALYTICS_KEY);
      const stored = localStorage.getItem(key);
      const analytics: Array<{ query: string; module: string; itemId: number | string; timestamp: number }> =
        stored ? JSON.parse(stored) : [];

      analytics.push({
        query,
        module: item.module,
        itemId: item.id,
        timestamp: Date.now()
      });

      // Keep last 200 entries
      const trimmed = analytics.length > 200 ? analytics.slice(-200) : analytics;
      localStorage.setItem(key, JSON.stringify(trimmed));
    } catch {
      // Silently fail — analytics should not break search
    }
  }

  /** Get module keys sorted by selection frequency (most popular first) */
  getPopularModules(): string[] {
    try {
      const stored = localStorage.getItem(this.userKey(ANALYTICS_KEY));
      if (!stored) return [];

      const analytics: Array<{ module: string }> = JSON.parse(stored);
      const counts = new Map<string, number>();
      for (const entry of analytics) {
        counts.set(entry.module, (counts.get(entry.module) || 0) + 1);
      }

      return [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([key]) => key);
    } catch {
      return [];
    }
  }

  /** Store a cache entry, evicting oldest if at capacity */
  private setCacheEntry(cacheKey: string, query: string, results: GlobalSearchModuleResult[]): void {
    if (this.cache.size >= MAX_CACHE_ENTRIES) {
      // Evict the oldest entry
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey !== undefined) {
        this.cache.delete(oldestKey);
      }
    }
    this.cache.set(cacheKey, { query, results, timestamp: Date.now() });
  }
}
